import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '../components/Avatar';
import PostCard from '../components/PostCard';
import { searchApi } from '../services/searchApi';
import { postApi } from '../services/postApi';
import './Search.css';

const PAGE_SIZE = 20;

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const searchResults = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : users.length;
      const results = await searchApi.search(query, PAGE_SIZE, offset);

      const newPosts = reset ? (results?.posts || []) : [...posts, ...(results?.posts || [])];

      if (reset) {
        setUsers(results?.users || []);
        setPosts(newPosts);
      } else {
        setUsers(prev => [...prev, ...(results?.users || [])]);
        setPosts(newPosts);
      }

      // Batch load comments for all posts
      const postIds = newPosts.map(p => p.id).filter(id => id);
      if (postIds.length > 0) {
        try {
          const commentsResponse = await postApi.getBatchComments(postIds);
          if (commentsResponse.data) {
            setPostComments(prev => ({ ...prev, ...commentsResponse.data }));
          }
        } catch (err) {
          console.error('Error loading batch comments:', err);
        }
      }

      // Check if we have more results
      const hasMoreResults = (results?.users?.length === PAGE_SIZE) || (results?.posts?.length === PAGE_SIZE);
      setHasMore(hasMoreResults);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, users.length]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setUsers([]);
      setPosts([]);
      setLoading(false);
      return;
    }

    searchResults(true);
  }, [query]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          searchResults(false);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, loadingMore, hasMore, searchResults]);

  const handleUserClick = (userId) => {
    window.location.href = `/profile/${userId}`;
  };

  if (loading) {
    return (
      <div className="search-page">
        <div className="search-loading">{t('common.loading')}</div>
      </div>
    );
  }

  const hasResults = users.length > 0 || posts.length > 0;

  return (
    <div className="search-page">
      <div className="search-header">
        <h2 className="search-title">{t('search.searchResults')}: "{query}"</h2>
      </div>

      {!hasResults && (
        <div className="search-no-results">{t('search.noResults')}</div>
      )}

      {hasResults && (
        <>
          <div className="search-tabs">
            <button
              className={`search-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              {t('search.all')}
            </button>
            <button
              className={`search-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              {t('search.users')}
            </button>
            <button
              className={`search-tab ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              {t('search.posts')}
            </button>
          </div>

          <div className="search-results">
            {(activeTab === 'all' || activeTab === 'users') && users.length > 0 && (
              <div className="search-users-list">
                {users.map(u => (
                  <div
                    key={u.id}
                    className="search-user-row"
                    onClick={() => handleUserClick(u.id)}
                  >
                    <Avatar user={u} size="medium" />
                    <div className="search-user-row-info">
                      <div className="search-user-name">{u.display_name || u.username}</div>
                      <div className="search-user-username">@{u.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(activeTab === 'all' || activeTab === 'posts') && posts.length > 0 && (
              <div className="search-section">
                {activeTab === 'all' && <h3 className="search-section-title">{t('search.posts')}</h3>}
                <div className="search-posts-list">
                  {posts.map(p => (
                    <PostCard
                      key={p.id}
                      post={p}
                      initialComments={postComments[p.id]}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Load more trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="search-load-more">
                {loadingMore && <div className="search-loading-more">{t('common.loading')}</div>}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Search;
